/**
 * Logs — deep, filterable log viewer for expert engineers (ADR-024).
 *
 * Filter bar: free-text q, agent, skill, level multi-toggle (error/warn/info/debug),
 * type multi-select, source multi-select (stdout only for fleet_owner), and a
 * time-range picker with presets + custom from/until.
 *
 * Results: dense, monospace-ish log list. Click a row to expand detail.
 *
 * Paging: "Load older" button uses next_cursor from the API.
 *
 * Live tail: polls closed insertion-fence windows every 2s, following each
 * window's cursor before advancing its fence. Late rows are merged by timestamp.
 *
 * RBAC: page is only reachable by operator|fleet_owner (nav is gated and we
 * also handle an unexpected 403 defensively).
 */
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useId,
} from 'react'
import { api, ForbiddenError } from '../api/client'
import type { LogRow, LogsParams, LogsStats } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { dedupeLogRows, logRowIdentity } from '../utils/logRowIdentity'
import { relativeTime } from '../utils/relativeTime'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 2_000
const DEFAULT_LIMIT = 100
const MAX_ROWS = 1_000

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Level = 'error' | 'warn' | 'info' | 'debug'
type Source = 'event' | 'agent' | 'stdout'

type TimePreset = 'last15m' | 'last1h' | 'last24h' | 'last7d' | 'custom'

interface TimeRange {
  preset: TimePreset
  since: string  // ISO string or empty
  until: string  // ISO string or empty
}

interface Filters {
  q: string
  agent: string
  skill: string
  levels: Set<Level>
  types: Set<string>
  sources: Set<Source>
  timeRange: TimeRange
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ALL_LEVELS: Level[] = ['error', 'warn', 'info', 'debug']
// Type = the semantic kind of a structured EVENT-tier row. The per-agent
// (agent_log) and raw (stdout) tiers are selected via SOURCE, not TYPE — listing
// them here too was a confusing 1:1 dupe of the source axis, so they're omitted.
// (Rows still display their own type in the table regardless of this filter.)
const ALL_TYPES = ['delegation', 'approval', 'signal_audit', 'grade', 'self_edit', 'experiment']
// Source = which pipe a row came from: event (signals.db governance), agent
// (per-agent action log), stdout (raw container firehose).
const ALL_SOURCES: Source[] = ['event', 'agent', 'stdout']

const TIME_PRESETS: { key: TimePreset; label: string }[] = [
  { key: 'last15m', label: 'Last 15m' },
  { key: 'last1h',  label: 'Last 1h' },
  { key: 'last24h', label: 'Last 24h' },
  { key: 'last7d',  label: 'Last 7d' },
  { key: 'custom',  label: 'Custom' },
]

function presetToSince(preset: TimePreset): string {
  if (preset === 'custom') return ''
  const now = Date.now()
  const offsetMs: Record<TimePreset, number> = {
    last15m: 15 * 60 * 1000,
    last1h:  60 * 60 * 1000,
    last24h: 24 * 60 * 60 * 1000,
    last7d:  7 * 24 * 60 * 60 * 1000,
    custom:  0,
  }
  return new Date(now - offsetMs[preset]).toISOString()
}

function buildParams(filters: Filters, cursor?: string): LogsParams {
  const params: LogsParams = { limit: DEFAULT_LIMIT }

  if (filters.q.trim())     params.q     = filters.q.trim()
  if (filters.agent.trim()) params.agent = filters.agent.trim()
  if (filters.skill.trim()) params.skill = filters.skill.trim()

  if (filters.levels.size > 0 && filters.levels.size < ALL_LEVELS.length) {
    params.level = [...filters.levels].join(',')
  }
  if (filters.types.size > 0) {
    params.type = [...filters.types].join(',')
  }
  if (filters.sources.size > 0 && filters.sources.size < ALL_SOURCES.length) {
    params.source = [...filters.sources].join(',')
  }

  const { preset, since, until } = filters.timeRange
  if (preset !== 'custom') {
    params.since = presetToSince(preset)
  } else {
    if (since) params.since = new Date(since).toISOString()
    if (until) params.until = new Date(until).toISOString()
  }

  if (cursor) params.cursor = cursor

  return params
}

function defaultFilters(): Filters {
  return {
    q: '',
    agent: '',
    skill: '',
    levels: new Set(),
    types: new Set(),
    sources: new Set(),
    timeRange: { preset: 'last1h', since: '', until: '' },
  }
}

// ---------------------------------------------------------------------------
// Level styling
// ---------------------------------------------------------------------------

interface LevelStyle {
  chip: string
  row: string
}

function levelStyle(level: string): LevelStyle {
  switch (level) {
    case 'error':
      return { chip: 'bg-red-100 text-red-800', row: 'bg-red-50' }
    case 'warn':
      return { chip: 'bg-amber-100 text-amber-800', row: 'bg-amber-50/40' }
    case 'info':
      return { chip: 'bg-gray-100 text-gray-600', row: '' }
    case 'debug':
      return { chip: 'bg-blue-50 text-blue-700', row: '' }
    default:
      return { chip: 'bg-gray-100 text-gray-600', row: '' }
  }
}

function levelToggleClass(level: Level, active: boolean): string {
  if (!active) return 'border border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
  switch (level) {
    case 'error': return 'border border-red-300 bg-red-100 text-red-800'
    case 'warn':  return 'border border-amber-300 bg-amber-100 text-amber-800'
    case 'info':  return 'border border-gray-300 bg-gray-100 text-gray-700'
    case 'debug': return 'border border-blue-300 bg-blue-100 text-blue-700'
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface LogRowItemProps {
  row: LogRow
  expanded: boolean
  onToggle: () => void
}

function LogRowItem({ row, expanded, onToggle }: LogRowItemProps) {
  const { chip, row: rowBg } = levelStyle(row.level)
  const abs = new Date(row.ts).toLocaleString()

  return (
    <li
      className={`border-b border-gray-100 last:border-0 ${rowBg}`}
    >
      {/* Main log line — ONLY this header toggles; the detail below stays open
          so its text can be selected/copied. */}
      <div
        className="px-3 py-1.5 flex items-start gap-2 font-mono text-xs cursor-pointer select-none hover:brightness-95"
        onClick={onToggle}
      >
        {/* Time */}
        <span
          className="shrink-0 text-gray-400 w-16 pt-px"
          title={abs}
        >
          {relativeTime(row.ts)}
        </span>

        {/* Level chip */}
        <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold uppercase w-12 justify-center ${chip}`}>
          {row.level}
        </span>

        {/* Agent */}
        <span className="shrink-0 text-indigo-700 w-24 truncate" title={row.agent ?? ''}>
          {row.agent ?? '—'}
        </span>

        {/* Type / skill */}
        <span className="shrink-0 text-gray-400 w-24 truncate" title={row.type ?? row.skill ?? ''}>
          {row.type ?? row.skill ?? '—'}
        </span>

        {/* Summary */}
        <span className="flex-1 text-gray-800 truncate">{row.summary}</span>

        {/* Source badge (subtle) */}
        <span className="shrink-0 text-gray-300 text-[10px] uppercase tracking-wide">{row.source}</span>

        {/* Expand indicator */}
        <span className="shrink-0 text-gray-300 w-3">{expanded ? '▾' : '▸'}</span>
      </div>

      {/* Expanded detail — clicks here do NOT collapse, and text is selectable
          so you can copy it. A Copy button grabs the whole row as text. */}
      {expanded && (
        <div
          className="px-3 pb-3 pt-1 bg-gray-50 border-t border-gray-100 select-text"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Metadata row + Copy button */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500 font-mono">
              <span><span className="font-semibold text-gray-600">ts:</span> {abs}</span>
              {row.logger       && <span><span className="font-semibold text-gray-600">logger:</span> {row.logger}</span>}
              {row.container    && <span><span className="font-semibold text-gray-600">container:</span> {row.container}</span>}
              {row.correlation_id && <span><span className="font-semibold text-gray-600">corr_id:</span> {row.correlation_id}</span>}
              {row.skill        && <span><span className="font-semibold text-gray-600">skill:</span> {row.skill}</span>}
              <span><span className="font-semibold text-gray-600">id:</span> {row.id ?? '—'}</span>
            </div>
            <CopyButton text={rowToText(row)} label="Copy" className="shrink-0" />
          </div>
          {row.detail ? (
            <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all font-mono bg-white border border-gray-200 rounded p-2 max-h-64 overflow-auto select-text">
              {row.detail}
            </pre>
          ) : (
            <p className="text-xs text-gray-400 italic">No detail available.</p>
          )}
        </div>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Copy / export helpers
// ---------------------------------------------------------------------------

/** One row → a readable multi-line text block (good for pasting into chat). */
function rowToText(r: LogRow): string {
  const meta = [
    `ts=${r.ts}`,
    `level=${r.level}`,
    `source=${r.source}`,
    r.agent ? `agent=${r.agent}` : '',
    r.type ? `type=${r.type}` : '',
    r.skill ? `skill=${r.skill}` : '',
    r.logger ? `logger=${r.logger}` : '',
    r.container ? `container=${r.container}` : '',
    r.correlation_id ? `corr_id=${r.correlation_id}` : '',
  ].filter(Boolean).join(' ')
  let out = `[${meta}] ${r.summary ?? ''}`
  if (r.detail && r.detail !== r.summary) out += `\n${r.detail}`
  return out
}

/** All currently-shown rows → readable text (oldest assumptions aside, as shown). */
function logsToText(rows: LogRow[]): string {
  return rows.map(rowToText).join('\n\n')
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

function downloadFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/** A small copy-to-clipboard button with transient "Copied!" feedback. */
function CopyButton({ text, label, className = '' }: { text: string; label: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        void copyText(text).then((ok) => {
          if (ok) {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          }
        })
      }}
      className={`text-xs font-medium px-2 py-0.5 rounded border transition-colors ${
        copied
          ? 'border-green-300 bg-green-50 text-green-700'
          : 'border-gray-300 text-gray-600 hover:bg-gray-100'
      } ${className}`}
    >
      {copied ? 'Copied!' : label}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Toggle helpers
// ---------------------------------------------------------------------------

function toggleSet<T>(s: Set<T>, v: T): Set<T> {
  const next = new Set(s)
  if (next.has(v)) next.delete(v)
  else next.add(v)
  return next
}

// ---------------------------------------------------------------------------
// PipelineHealth strip (fleet_owner only)
// ---------------------------------------------------------------------------

interface TierChipProps {
  label: string
  count: number
  lastTs: string | null
  hint?: string
  status: 'green' | 'amber' | 'red' | 'gray'
}

function TierChip({ label, count, lastTs, hint, status }: TierChipProps) {
  const dotClass =
    status === 'green' ? 'bg-green-500' :
    status === 'amber' ? 'bg-amber-400' :
    status === 'red'   ? 'bg-red-500' :
    'bg-gray-300'

  const borderClass =
    status === 'green' ? 'border-green-200 bg-green-50' :
    status === 'amber' ? 'border-amber-200 bg-amber-50' :
    status === 'red'   ? 'border-red-200 bg-red-50' :
    'border-gray-200 bg-gray-50'

  return (
    <div className={`rounded border px-3 py-2 text-xs ${borderClass}`}>
      <div className="flex items-center gap-1.5 font-semibold text-gray-700">
        <span className={`inline-block w-2 h-2 rounded-full ${dotClass}`} />
        <span>{label}</span>
      </div>
      <div className="mt-1 text-gray-600">
        <span className="font-mono">{count}</span> rows/24h
        {lastTs && (
          <span className="ml-2 text-gray-400">
            last: {relativeTime(lastTs)}
          </span>
        )}
      </div>
      {hint && (
        <div className="mt-1 text-gray-500 italic">{hint}</div>
      )}
    </div>
  )
}

interface PipelineHealthProps {
  stats: LogsStats
}

function PipelineHealth({ stats }: PipelineHealthProps) {
  const [open, setOpen] = React.useState(true)

  const { logs_db, ingest_http, agent_tail, tier1 } = stats

  // Tier 1 — live count and timestamp from signals.db via the upstream.
  const t1Count  = tier1.rows_24h ?? logs_db.rows_24h_by_source.event
  const t1LastTs = tier1.last_ts !== undefined ? tier1.last_ts : logs_db.last_ts_by_source.event
  const t1Status: 'green' | 'gray' = t1Count > 0 ? 'green' : 'gray'

  // Tier 2 — agent NDJSON tailer
  const t2Count  = logs_db.rows_24h_by_source.agent
  const t2LastTs = logs_db.last_ts_by_source.agent
  const t2Status: 'green' | 'red' = t2Count > 0 ? 'green' : 'red'
  const t2Hint = t2Count === 0
    ? 'no agent NDJSON ingested (runtime may not log at this LOG_LEVEL)'
    : undefined

  // Tier 3 — Vector stdout firehose
  const t3Count  = logs_db.rows_24h_by_source.stdout
  const t3LastTs = logs_db.last_ts_by_source.stdout
  let t3Status: 'green' | 'amber' | 'red' = 'green'
  let t3Hint: string | undefined
  if (ingest_http.posts_received === 0) {
    t3Status = 'red'
    t3Hint = 'Vector not POSTing (shipper/socket/config)'
  } else if (ingest_http.rows_inserted === 0) {
    t3Status = 'amber'
    t3Hint = 'ingested but stored 0 (parse/redact)'
  } else if (t3Count === 0) {
    t3Status = 'amber'
    t3Hint = 'posts received but no rows in 24h window'
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      {/* Header */}
      <button
        type="button"
        className="flex w-full items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900"
        onClick={() => setOpen(v => !v)}
      >
        <span>Pipeline health</span>
        <span className="text-gray-400 text-xs">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          {/* Tier chips row */}
          <div className="flex flex-wrap gap-2">
            <TierChip
              label="Events (Tier 1)"
              count={t1Count}
              lastTs={t1LastTs}
              status={t1Status}
            />
            <TierChip
              label="Agent log (Tier 2)"
              count={t2Count}
              lastTs={t2LastTs}
              hint={t2Hint}
              status={t2Status}
            />
            <TierChip
              label="Stdout firehose (Tier 3)"
              count={t3Count}
              lastTs={t3LastTs}
              hint={t3Hint}
              status={t3Status}
            />
          </div>

          {/* Tier-3 detail row (posts/parsed/inserted) */}
          <div className="text-xs text-gray-500 flex flex-wrap gap-x-4">
            <span>
              Tier-3 HTTP:{' '}
              <span className="font-mono text-gray-700">{ingest_http.posts_received}</span> posts,{' '}
              <span className="font-mono text-gray-700">{ingest_http.events_parsed}</span> parsed,{' '}
              <span className="font-mono text-gray-700">{ingest_http.rows_inserted}</span> stored
              {ingest_http.rows_skipped > 0 && (
                <span className="text-amber-600 ml-1">({ingest_http.rows_skipped} skipped)</span>
              )}
            </span>
            <span>
              Tier-2 tailer:{' '}
              <span className="font-mono text-gray-700">{agent_tail.files_seen}</span> files,{' '}
              <span className="font-mono text-gray-700">{agent_tail.lines_ingested}</span> lines
            </span>
            <span>
              Tier-1 delegations:{' '}
              <span className="font-mono text-gray-700">{tier1.delegation_24h}</span>/24h
            </span>
          </div>

          {/* Generated at */}
          <p className="text-[10px] text-gray-300">
            as of {new Date(stats.generated_at).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function Logs() {
  const { user } = useAuth()
  const isOwner = user.fleet_role === 'fleet_owner'

  // ------ filter state ------
  const [filters, setFilters] = useState<Filters>(defaultFilters)

  // ------ log list ------
  const [rows, setRows] = useState<LogRow[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)

  // ------ UI state ------
  const [loading, setLoading] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [liveTail, setLiveTail] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [newCount, setNewCount] = useState(0)

  // ------ pipeline health (fleet_owner only) ------
  const [healthStats, setHealthStats] = useState<LogsStats | null>(null)
  const [healthLoading, setHealthLoading] = useState(false)

  // Ref for the scroll container — used to detect if user is at top.
  const listRef = useRef<HTMLDivElement>(null)

  const tailRef = useRef<{ after: string | null; through: string | null; cursor: string | null }>({
    after: null, through: null, cursor: null,
  })
  const tailInFlightRef = useRef(false)
  const loadGenerationRef = useRef(0)

  // Stable ref to filters so the tail interval doesn't stale-close.
  const filtersRef = useRef<Filters>(filters)
  useEffect(() => {
    filtersRef.current = filters
  })

  // a11y ids
  const qId   = useId()
  const agId  = useId()
  const skId  = useId()
  const sinId = useId()
  const untId = useId()

  // ------ initial / filter-change load ------

  const loadFresh = useCallback(async (currentFilters: Filters) => {
    const generation = ++loadGenerationRef.current
    setLoading(true)
    setError(null)
    setForbidden(false)
    setExpandedIds(new Set())
    setNewCount(0)
    try {
      const res = await api.getLogs(buildParams(currentFilters))
      if (generation !== loadGenerationRef.current) return
      setRows(res.logs)
      setNextCursor(res.next_cursor)
      tailRef.current = { after: res.ingest_fence ?? null, through: null, cursor: null }
    } catch (err) {
      if (generation !== loadGenerationRef.current) return
      if (err instanceof ForbiddenError) {
        setForbidden(true)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load logs.')
      }
    } finally {
      if (generation === loadGenerationRef.current) setLoading(false)
    }
  }, [])

  // Load on mount and whenever filters change (debounced via useEffect deps).
  useEffect(() => {
    void loadFresh(filters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.q,
    filters.agent,
    filters.skill,
    // Serialize set-valued fields so the effect fires on content change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...filters.levels].sort().join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...filters.types].sort().join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...filters.sources].sort().join(','),
    filters.timeRange.preset,
    filters.timeRange.since,
    filters.timeRange.until,
    loadFresh,
  ])

  const loadHealthStats = useCallback(async () => {
    if (!isOwner) return
    setHealthLoading(true)
    try {
      const stats = await api.getLogsStats()
      setHealthStats(stats)
    } catch {
      // 403 or unavailable — hide panel gracefully
      setHealthStats(null)
    } finally {
      setHealthLoading(false)
    }
  }, [isOwner])

  useEffect(() => {
    void loadHealthStats()
  }, [loadHealthStats])

  // ------ load older (pagination) ------

  const handleLoadOlder = useCallback(async () => {
    if (!nextCursor || loadingOlder) return
    setLoadingOlder(true)
    try {
      const res = await api.getLogs(buildParams(filters, nextCursor))
      setRows(prev => dedupeLogRows([...prev, ...res.logs]))
      setNextCursor(res.next_cursor)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load older logs.')
    } finally {
      setLoadingOlder(false)
    }
  }, [filters, nextCursor, loadingOlder])

  // ------ live tail ------

  useEffect(() => {
    if (!liveTail) {
      setNewCount(0)
      return
    }

    const interval = setInterval(async () => {
      if (tailInFlightRef.current) return
      tailInFlightRef.current = true
      const generation = loadGenerationRef.current
      try {
        const currentFilters = filtersRef.current
        const { after } = tailRef.current
        let { through, cursor } = tailRef.current

        // A failed first load has no starting fence. Establish a fresh position;
        // subsequent ticks can then drain commits made after that point.
        if (after === null) {
          const params = buildParams(currentFilters)
          params.limit = 1
          const res = await api.getLogs(params)
          if (generation !== loadGenerationRef.current) return
          tailRef.current = { after: res.ingest_fence ?? null, through: null, cursor: null }
          return
        }

        for (let pageNo = 0; pageNo < 5; pageNo++) {
          const params = buildParams(currentFilters, cursor ?? undefined)
          params.fence_after = after
          if (through !== null) params.fence_through = through

          const res = await api.getLogs(params)
          if (generation !== loadGenerationRef.current) return

          if (through === null) {
            through = res.ingest_fence ?? after
            tailRef.current = { after, through, cursor }
          }

          if (res.logs.length > 0) {
            setRows(prev => {
              if (generation !== loadGenerationRef.current) return prev
              const incoming = dedupeLogRows(res.logs, new Set(prev.map(logRowIdentity)))
              if (incoming.length === 0) return prev

              const combined = [...incoming, ...prev]
                .sort((a, b) => b.ts.localeCompare(a.ts))
                .slice(0, MAX_ROWS)
              const el = listRef.current
              const atTop = !el || el.scrollTop < 80
              if (atTop) {
                requestAnimationFrame(() => listRef.current?.scrollTo({ top: 0, behavior: 'smooth' }))
                setNewCount(0)
              } else {
                setNewCount(n => n + incoming.length)
              }
              return combined
            })
          }

          cursor = res.next_cursor
          if (cursor === null) {
            tailRef.current = { after: through, through: null, cursor: null }
            return
          }
          tailRef.current = { after, through, cursor }
        }
      } catch {
        // Keep the current window and cursor so the next tick retries this page.
      } finally {
        tailInFlightRef.current = false
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [liveTail])

  // ------ expand / collapse ------

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // ------ filter setters ------

  const setQ     = (v: string) => setFilters(f => ({ ...f, q: v }))
  const setAgent = (v: string) => setFilters(f => ({ ...f, agent: v }))
  const setSkill = (v: string) => setFilters(f => ({ ...f, skill: v }))

  const toggleLevel  = (l: Level)  => setFilters(f => ({ ...f, levels:  toggleSet(f.levels, l) }))
  const toggleType   = (t: string) => setFilters(f => ({ ...f, types:   toggleSet(f.types, t) }))
  const toggleSource = (s: Source) => setFilters(f => ({ ...f, sources: toggleSet(f.sources, s) }))

  const setPreset = (p: TimePreset) =>
    setFilters(f => ({ ...f, timeRange: { preset: p, since: f.timeRange.since, until: f.timeRange.until } }))

  const setSince = (v: string) =>
    setFilters(f => ({ ...f, timeRange: { ...f.timeRange, since: v } }))

  const setUntil = (v: string) =>
    setFilters(f => ({ ...f, timeRange: { ...f.timeRange, until: v } }))

  const handleReset = () => {
    setFilters(defaultFilters())
    setLiveTail(false)
  }

  // ------ render ------

  if (forbidden) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900">Logs</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-6 text-sm text-red-700">
          <p className="font-semibold">Access denied</p>
          <p className="mt-1">The Logs view requires Operator or Fleet Owner role.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Page heading */}
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Logs</h1>
          <p className="text-sm text-gray-500 mt-1">
            Filterable view of agent events, structured logs, and raw output.
          </p>
        </div>
        {liveTail && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            Live
          </span>
        )}
      </div>

      {/* Pipeline health strip — fleet_owner only */}
      {isOwner && healthStats && (
        <PipelineHealth stats={healthStats} />
      )}
      {isOwner && healthLoading && !healthStats && (
        <div className="text-xs text-gray-400 animate-pulse">Loading pipeline health…</div>
      )}
      {/* Refresh button for health when not live-tailing */}
      {isOwner && healthStats && !liveTail && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => void loadHealthStats()}
            disabled={healthLoading}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            Refresh health
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 space-y-3">
        {/* Row 1: text inputs + live tail */}
        <div className="flex flex-wrap gap-2 items-end">
          {/* Free-text search */}
          <div className="flex flex-col gap-0.5">
            <label htmlFor={qId} className="text-xs font-medium text-gray-500">Search</label>
            <input
              id={qId}
              type="text"
              value={filters.q}
              onChange={e => setQ(e.target.value)}
              placeholder="keyword…"
              className="h-8 w-48 rounded border border-gray-200 px-2 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          {/* Agent */}
          <div className="flex flex-col gap-0.5">
            <label htmlFor={agId} className="text-xs font-medium text-gray-500">Agent</label>
            <input
              id={agId}
              type="text"
              value={filters.agent}
              onChange={e => setAgent(e.target.value)}
              placeholder="agent name…"
              className="h-8 w-36 rounded border border-gray-200 px-2 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          {/* Skill */}
          <div className="flex flex-col gap-0.5">
            <label htmlFor={skId} className="text-xs font-medium text-gray-500">Skill</label>
            <input
              id={skId}
              type="text"
              value={filters.skill}
              onChange={e => setSkill(e.target.value)}
              placeholder="skill name…"
              className="h-8 w-36 rounded border border-gray-200 px-2 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          <div className="flex-1" />

          {/* Live tail toggle */}
          <button
            type="button"
            onClick={() => setLiveTail(v => !v)}
            className={`h-8 px-3 rounded text-sm font-medium transition-colors ${
              liveTail
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {liveTail ? 'Live tail ON' : 'Live tail'}
          </button>

          {/* Reset */}
          <button
            type="button"
            onClick={handleReset}
            className="h-8 px-3 rounded text-sm font-medium text-gray-500 border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
          >
            Reset
          </button>

          {/* Manual refresh (when not in live tail) */}
          {!liveTail && (
            <button
              type="button"
              onClick={() => void loadFresh(filters)}
              disabled={loading}
              className="h-8 px-3 rounded text-sm font-medium text-indigo-700 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
            >
              Refresh
            </button>
          )}
        </div>

        {/* Row 2: level toggles + type multi + source multi */}
        <div className="flex flex-wrap gap-3 items-start">
          {/* Level multi-toggle */}
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Level</span>
            <div className="flex gap-1">
              {ALL_LEVELS.map(l => (
                <button
                  key={l}
                  type="button"
                  onClick={() => toggleLevel(l)}
                  className={`h-7 px-2 rounded text-xs font-semibold uppercase transition-colors ${levelToggleClass(l, filters.levels.has(l))}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Type multi */}
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Type</span>
            <div className="flex flex-wrap gap-1">
              {ALL_TYPES.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleType(t)}
                  className={`h-7 px-2 rounded text-xs font-medium transition-colors ${
                    filters.types.has(t)
                      ? 'bg-indigo-100 text-indigo-800 border border-indigo-300'
                      : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Source multi — stdout only for fleet_owner */}
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Source</span>
            <div className="flex gap-1">
              {ALL_SOURCES.filter(s => s !== 'stdout' || isOwner).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSource(s)}
                  className={`h-7 px-2 rounded text-xs font-medium transition-colors ${
                    filters.sources.has(s)
                      ? 'bg-indigo-100 text-indigo-800 border border-indigo-300'
                      : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3: time range presets + custom inputs */}
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Time range</span>
            <div className="flex gap-1">
              {TIME_PRESETS.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`h-7 px-2.5 rounded text-xs font-medium transition-colors ${
                    filters.timeRange.preset === p.key
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {filters.timeRange.preset === 'custom' && (
            <>
              <div className="flex flex-col gap-0.5">
                <label htmlFor={sinId} className="text-xs font-medium text-gray-500">From</label>
                <input
                  id={sinId}
                  type="datetime-local"
                  value={filters.timeRange.since}
                  onChange={e => setSince(e.target.value)}
                  className="h-8 rounded border border-gray-200 px-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <label htmlFor={untId} className="text-xs font-medium text-gray-500">Until</label>
                <input
                  id={untId}
                  type="datetime-local"
                  value={filters.timeRange.until}
                  onChange={e => setUntil(e.target.value)}
                  className="h-8 rounded border border-gray-200 px-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* "New rows above" notification when user scrolled down during live tail */}
      {newCount > 0 && (
        <button
          type="button"
          onClick={() => {
            listRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
            setNewCount(0)
          }}
          className="w-full text-center text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded py-1.5 hover:bg-green-100 transition-colors"
        >
          {newCount} new {newCount === 1 ? 'entry' : 'entries'} — click to scroll to top
        </button>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="text-sm text-gray-400 animate-pulse">Loading logs…</div>
      )}

      {/* Log list */}
      {!loading && (
        <div
          ref={listRef}
          className="bg-white rounded-lg border border-gray-200 overflow-hidden overflow-y-auto max-h-[65vh]"
        >
          {rows.length === 0 && !error ? (
            <p className="px-5 py-10 text-sm text-gray-400 text-center">
              {nextCursor ? 'No matching logs in the range searched so far.' : 'No logs match the current filters.'}
            </p>
          ) : (
            <ul className="divide-y-0">
              {rows.map(row => {
                const k = logRowIdentity(row)
                return (
                  <LogRowItem
                    key={k}
                    row={row}
                    expanded={expandedIds.has(k)}
                    onToggle={() => toggleExpand(k)}
                  />
                )
              })}
            </ul>
          )}
        </div>
      )}

      {!loading && rows.length === 0 && nextCursor && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => void handleLoadOlder()}
            disabled={loadingOlder}
            className="text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50 transition-colors"
          >
            {loadingOlder ? 'Loading…' : 'Load older'}
          </button>
        </div>
      )}

      {/* Load older / pagination */}
      {!loading && rows.length > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center gap-3">
            <span>
              Showing {rows.length} {rows.length >= MAX_ROWS ? `(capped at ${MAX_ROWS})` : ''} entries
            </span>
            {/* Export the currently-shown logs (matches the active filters). */}
            <CopyButton text={logsToText(rows)} label="Copy all" />
            <button
              type="button"
              onClick={() => downloadFile(`mc-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.json`, JSON.stringify(rows, null, 2), 'application/json')}
              className="text-xs font-medium px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Export .json
            </button>
            <button
              type="button"
              onClick={() => downloadFile(`mc-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`, logsToText(rows), 'text/plain')}
              className="text-xs font-medium px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Export .txt
            </button>
          </div>
          {nextCursor ? (
            <button
              type="button"
              onClick={() => void handleLoadOlder()}
              disabled={loadingOlder}
              className="text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50 transition-colors"
            >
              {loadingOlder ? 'Loading…' : 'Load older'}
            </button>
          ) : (
            <span className="text-gray-300">End of results</span>
          )}
        </div>
      )}
    </div>
  )
}
